import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Calendar, ChevronLeft, ChevronRight, Download, FileText, FileSpreadsheet, Sprout, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composting schedule milestones with colors
const COMPOSTING_MILESTONES = [
  { day: 1, task: 'Prewetting Start', color: '#16A34A' },
  { day: 4, task: 'C.M/Gypsum/Urea Mixing', color: '#F59E0B' },
  { day: 5, task: 'Bunker Filling', color: '#D97706' },
  { day: 7, task: '1st Turning', color: '#3B82F6' },
  { day: 10, task: '2nd Turning', color: '#6366F1' },
  { day: 12, task: '3rd Turning', color: '#8B5CF6' },
  { day: 14, task: 'Tunnel Filling', color: '#EC4899' },
  { day: 15, task: 'Pasturization Start', color: '#EF4444' },
  { day: 20, task: 'Spawning Ready', color: '#A855F7' },
];

const CompostingCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Compute next 10 prewetting (Day 1) start dates based on the most recent
  // batch and the 3-day cadence used by the farm.
  const nextPrewettingDates = useMemo(() => {
    if (!batches || batches.length === 0) return [];

    // Find the latest start_date among all batches.
    const startDates = batches
      .map((b) => b.start_date && new Date(b.start_date))
      .filter(Boolean)
      .sort((a, b) => b - a);

    const latest = startDates[0];
    if (!latest) return [];

    // Determine cadence in days (default 3) by looking at the two most recent.
    let cadence = 3;
    if (startDates.length >= 2) {
      const diff = Math.round((startDates[0] - startDates[1]) / (1000 * 60 * 60 * 24));
      if (diff > 0 && diff <= 14) cadence = diff;
    }

    const out = [];
    let next = addDays(latest, cadence);
    for (let i = 0; i < 10; i++) {
      out.push({
        idx: i + 1,
        prewettingDate: next,
        spawningDate: addDays(next, 19),
      });
      next = addDays(next, cadence);
    }
    return out;
  }, [batches]);

  const handleExport = async (exportFormat) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/composting-calendar?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `composting_schedule.${exportFormat === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Calendar exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export calendar');
    } finally {
      setExporting(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const batchesRes = await axios.get(`${API}/compost/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBatches(batchesRes.data);
      
      // Generate calendar events from batches
      const calendarEvents = [];
      const today = new Date();
      
      batchesRes.data.forEach(batch => {
        const startDate = new Date(batch.start_date);
        
        // Add all composting milestones for this batch
        COMPOSTING_MILESTONES.forEach(milestone => {
          const eventDate = addDays(startDate, milestone.day - 1);
          calendarEvents.push({
            id: `${batch.id}-${milestone.task}`,
            date: eventDate,
            title: `${batch.batch_name} - ${milestone.task}`,
            batchName: batch.batch_name,
            task: milestone.task,
            color: milestone.color,
            type: 'composting'
          });
        });
      });
      
      setEvents(calendarEvents);
      
    } catch (error) {
      // Silent fail - empty calendar will be shown
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => isSameDay(new Date(event.date), day));
  };

  const days = getDaysInMonth();
  const firstDayOfMonth = startOfMonth(currentMonth).getDay();

  return (
    <div className="space-y-6">
      {/* Calendar */}
      <Card data-testid="composting-calendar">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Composting Schedule Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExport('excel')}
              disabled={exporting}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <div className="border-l h-6 mx-2" />
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium px-4">{format(currentMonth, 'MMMM yyyy')}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading calendar...</div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[100px] bg-gray-50 rounded" />
                ))}
                
                {/* Days of the month */}
                {days.map(day => {
                  const dayEvents = getEventsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  const isSelected = selectedDay && isSameDay(day, selectedDay);
                  const hasEvents = dayEvents.length > 0;

                  return (
                    <button
                      type="button"
                      key={day.toISOString()}
                      onClick={() => setSelectedDay(day)}
                      className={`text-left min-h-[100px] p-1 border rounded transition-colors ${
                        isSelected
                          ? 'border-[#1A4D2E] bg-[#1A4D2E]/10 ring-2 ring-[#1A4D2E]/30'
                          : isToday
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-[#1A4D2E]/50 hover:bg-gray-50'
                      } ${!isSameMonth(day, currentMonth) ? 'opacity-50' : ''} ${
                        hasEvents ? 'cursor-pointer' : 'cursor-default'
                      }`}
                      data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1 overflow-y-auto max-h-[80px]">
                        {dayEvents.slice(0, 3).map((event) => (
                          <div
                            key={event.id}
                            className="text-xs p-1 rounded truncate"
                            style={{ backgroundColor: `${event.color}20`, borderLeft: `3px solid ${event.color}` }}
                            title={event.title}
                          >
                            {event.task}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} more</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-3">Legend</p>
                <div className="flex flex-wrap gap-3">
                  {COMPOSTING_MILESTONES.map((milestone) => (
                    <div key={milestone.task} className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: milestone.color }} />
                      <span className="text-xs text-gray-600">{milestone.task}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Selected-day details */}
      {selectedDay && !loading && (
        <Card data-testid="selected-day-details" className="border-[#1A4D2E]/30">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Work on {format(selectedDay, 'EEEE, MMMM d, yyyy')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDay(null)}
              data-testid="close-selected-day"
            >
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {(() => {
              const dayEvents = getEventsForDay(selectedDay);
              if (dayEvents.length === 0) {
                return (
                  <p className="text-sm text-gray-500 py-4" data-testid="no-work-message">
                    No composting work scheduled for this day.
                  </p>
                );
              }
              return (
                <div className="space-y-2">
                  {dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border"
                      style={{ borderLeftColor: event.color, borderLeftWidth: 4 }}
                      data-testid={`day-event-${event.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{event.batchName}</p>
                        <p className="text-xs text-gray-600 truncate">{event.task}</p>
                      </div>
                      <Badge
                        className="text-white border-0"
                        style={{ backgroundColor: event.color }}
                      >
                        {event.task}
                      </Badge>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Next 10 prewetting dates */}
      {nextPrewettingDates.length > 0 && (
        <Card data-testid="next-prewetting-dates">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sprout className="w-4 h-4 text-[#16A34A]" />
              Next 10 Prewetting Dates
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Auto-projected from the latest batch's start date and the recent cadence
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {nextPrewettingDates.map((p) => (
                <div
                  key={p.idx}
                  className="p-3 rounded-lg border border-[#16A34A]/30 bg-[#16A34A]/5"
                  data-testid={`prewetting-${p.idx}`}
                >
                  <div className="text-xs text-gray-500">Batch #{p.idx}</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {format(p.prewettingDate, 'EEE, dd MMM')}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    Spawns {format(p.spawningDate, 'dd MMM')}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompostingCalendar;
